import { alpha, createTheme, lighten } from '@mui/material/styles'

import { govioTheme } from '@/theme'

/**
 * UX-2.1 — Identidad visual oficial de GOVIA Client Application.
 *
 * Tema anidado del cliente, mismo patrón que `super-admin/theme.ts`
 * (`createTheme(govioTheme, {...})`, montado vía <ThemeProvider> DENTRO
 * de `AppShellV2`, nunca reemplazando el ThemeProvider raíz) — así V1
 * clásico y Super Admin conservan exactamente su apariencia actual
 * (paleta y tipografía sin cambios), y solo el shell V2 (el "Product
 * Shell" de este encargo) adopta la identidad oficial aprobada:
 *
 *   Fondo primario     #0D111A
 *   Fondo secundario   #0F1B2E
 *   Azul GOVIA         #2563FF
 *   Cian               #00C2FF
 *   Gris neutro        #A7B3C7
 *   Neutro claro       #E6EBF2
 *   Tipografía         Space Grotesk (cargada en index.html, ver ahí)
 *
 * Los valores de `light`/`dark`/`contrastText` de primary/secondary NO
 * se inventan — se dejan que MUI los derive automáticamente
 * (`augmentColor`) a partir del único hex oficial de cada color, igual
 * que hace `createTheme` con cualquier paleta parcial.
 */
const spaceGroteskFontFamily = [
  '"Space Grotesk"',
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(', ')

const OFFICIAL = {
  bgPrimary: '#0D111A',
  bgSecondary: '#0F1B2E',
  blue: '#2563FF',
  cyan: '#00C2FF',
  neutralGray: '#A7B3C7',
  neutralLight: '#E6EBF2',
} as const

export const clientTheme = createTheme(govioTheme, {
  palette: {
    primary: {
      main: OFFICIAL.blue,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: OFFICIAL.cyan,
      contrastText: '#04121A',
    },
    background: {
      default: OFFICIAL.bgPrimary,
      paper: OFFICIAL.bgSecondary,
    },
    divider: alpha(OFFICIAL.neutralGray, 0.16),
    text: {
      primary: OFFICIAL.neutralLight,
      secondary: OFFICIAL.neutralGray,
      disabled: alpha(OFFICIAL.neutralGray, 0.55),
    },
  },
  typography: {
    fontFamily: spaceGroteskFontFamily,
  },
})

/**
 * Superficies GOVIA V2 — mismo rol que `surfaces` en `theme/palette.ts`
 * (tonos fuera de la paleta MUI estándar: canvas, sidebar, bordes,
 * gradientes de marca), pero derivados de la identidad oficial. Nada
 * aquí es un color inventado: `cardElevated` es `bgSecondary` aclarado
 * un paso (`lighten`, función estándar de MUI, no un hex nuevo);
 * `border`/`borderStrong` son el gris neutro oficial a baja opacidad
 * (mismo patrón de alpha ya usado en `theme/palette.ts`).
 */
export const clientSurfaces = {
  canvas: OFFICIAL.bgPrimary,
  sidebar: OFFICIAL.bgPrimary,
  card: OFFICIAL.bgSecondary,
  cardElevated: lighten(OFFICIAL.bgSecondary, 0.06),
  border: alpha(OFFICIAL.neutralGray, 0.12),
  borderStrong: alpha(OFFICIAL.neutralGray, 0.22),
  gradientPrimary: `linear-gradient(135deg, ${OFFICIAL.blue} 0%, ${OFFICIAL.cyan} 100%)`,
  gradientSubtle: `linear-gradient(180deg, ${alpha(OFFICIAL.blue, 0.08)} 0%, rgba(0,0,0,0) 100%)`,
} as const
